import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const ProductsContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts must be used within ProductsProvider');
  }
  return context;
};

const INITIAL_PAGINATION = {
  page: 1,
  limit: 12,
  total: 0,
  totalPages: 0,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_LIST_PARAMS = {
  page: 1,
  limit: 12,
  sort: 'newest',
  search: '',
  category: '',
};

async function fetchCategoriesData() {
  const response = await api.get('/products/categories');
  return response.data;
}

async function fetchProductList(listParams) {
  const queryParams = new URLSearchParams({
    page: String(listParams.page),
    limit: String(listParams.limit),
    sort: listParams.sort,
    ...(listParams.search && { search: listParams.search }),
    ...(listParams.category && { category: listParams.category }),
  });
  const response = await api.get(`/products?${queryParams}`);
  return response.data;
}

export const ProductsProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [listParams, setListParams] = useState(() => ({ ...DEFAULT_LIST_PARAMS }));

  const categoriesQuery = useQuery({
    queryKey: ['products', 'categories'],
    queryFn: fetchCategoriesData,
    staleTime: 5 * 60 * 1000,
  });

  const productsQuery = useQuery({
    queryKey: ['products', 'list', listParams],
    queryFn: () => fetchProductList(listParams),
    staleTime: 5 * 60 * 1000,
  });

  const categories = categoriesQuery.data?.categories ?? [];
  const products = productsQuery.data?.products ?? [];
  const pagination = productsQuery.data?.pagination ?? INITIAL_PAGINATION;
  const loading = productsQuery.isFetching || categoriesQuery.isFetching;
  const error = productsQuery.isError
    ? productsQuery.error?.response?.data?.message ||
      productsQuery.error?.message ||
      'Failed to fetch products'
    : null;

  const filters = useMemo(
    () => ({
      search: listParams.search,
      category: listParams.category,
      sort: listParams.sort,
    }),
    [listParams.search, listParams.category, listParams.sort]
  );

  const fetchCategories = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: ['products', 'categories'] });
  }, [queryClient]);

  const fetchProducts = useCallback((params = {}) => {
    setListParams((prev) => ({
      page: params.page ?? prev.page,
      limit: params.limit ?? prev.limit,
      sort: params.sort ?? prev.sort,
      search: params.search !== undefined ? params.search || '' : prev.search,
      category: params.category !== undefined ? params.category || '' : prev.category,
    }));
  }, []);

  const updateFilters = useCallback((newFilters) => {
    setListParams((prev) => ({
      search: newFilters.search !== undefined ? newFilters.search || '' : prev.search,
      category: newFilters.category !== undefined ? newFilters.category || '' : prev.category,
      sort: newFilters.sort !== undefined ? newFilters.sort : prev.sort,
      limit: newFilters.limit !== undefined ? newFilters.limit : prev.limit,
      page: newFilters.page != null ? newFilters.page : 1,
    }));
  }, []);

  const changePage = useCallback((page) => {
    setListParams((prev) => ({ ...prev, page }));
  }, []);

  const value = {
    products,
    categories,
    loading,
    error,
    pagination,
    filters,
    fetchProducts,
    fetchCategories,
    updateFilters,
    changePage,
  };

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};
